import { z } from "zod";
import { geoPlaceSchema, type BirthData, type GeoPlace, type TimeAccuracy } from "@/lib/api/models/birth";

/**
 * The birth details as they are being entered, before a chart exists.
 *
 * Kept as plain data with pure updaters so the flow can be tested without a
 * DOM and so the draft can be persisted verbatim across the Google redirect.
 * `date` is an ISO calendar date; `minutes` is minutes since midnight.
 */
export const draftSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().catch(null),
  minutes: z.number().int().min(0).max(1439).nullable().catch(null),
  accuracy: z.enum(["exact", "approximate", "unknown"]).catch("exact"),
  place: geoPlaceSchema.nullable().catch(null),
  query: z.string().catch(""),
  consented: z.boolean().catch(false),
});
export type Draft = Readonly<z.output<typeof draftSchema>>;

export const EMPTY_DRAFT: Draft = Object.freeze({
  date: null,
  minutes: null,
  accuracy: "exact",
  place: null,
  query: "",
  consented: false,
});

/** Noon: what the engine uses when the time is unknown. */
const MIDDAY_MINUTES = 12 * 60;

export function setDate(draft: Draft, date: string | null): Draft {
  return Object.freeze({ ...draft, date });
}

export function setMinutes(draft: Draft, minutes: number | null): Draft {
  return Object.freeze({ ...draft, minutes });
}

export function setAccuracy(draft: Draft, accuracy: TimeAccuracy): Draft {
  return Object.freeze({ ...draft, accuracy });
}

export function setQuery(draft: Draft, query: string): Draft {
  // Typing again means the earlier choice no longer stands.
  return Object.freeze({ ...draft, query, place: null });
}

export function setPlace(draft: Draft, place: GeoPlace | null): Draft {
  return Object.freeze({ ...draft, place });
}

export function setConsented(draft: Draft, consented: boolean): Draft {
  return Object.freeze({ ...draft, consented });
}

export function hasDate(draft: Draft): boolean {
  return draft.date !== null;
}

/** "I don't know" is a complete answer: the engine falls back to noon. */
export function hasTime(draft: Draft): boolean {
  return draft.minutes !== null || draft.accuracy === "unknown";
}

export function hasPlace(draft: Draft): boolean {
  return draft.place !== null;
}

export function isComplete(draft: Draft): boolean {
  return hasDate(draft) && hasTime(draft) && hasPlace(draft);
}

/** The wire payload, or null while any of the three questions is unanswered. */
export function toBirthData(draft: Draft): BirthData | null {
  if (!draft.date || !draft.place) return null;
  if (!hasTime(draft)) return null;
  const [year, month, day] = draft.date.split("-").map(Number) as [number, number, number];
  const minutes = draft.minutes ?? MIDDAY_MINUTES;
  return Object.freeze({
    year,
    month,
    day,
    hour: Math.floor(minutes / 60),
    minute: minutes % 60,
    time_accuracy: draft.accuracy,
    lat: draft.place.lat,
    lon: draft.place.lon,
    resolved_tz: draft.place.tz,
    raw_place_text: draft.query || draft.place.name,
    geonames_id: draft.place.geonames_id,
  });
}
